import type React from "react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavLink {
  path: string;
  label: string;
}

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navLinks: NavLink[];
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, navLinks }) => {
  const location = useLocation();

  // Close menu on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally watch location.pathname to close menu on navigation
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const isActive = (path: string) => location.pathname === path;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[var(--z-modal-backdrop)] transition-opacity duration-[var(--transition-base)]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Mobile Menu Drawer */}
      <div
        className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-[var(--color-surface)] z-[var(--z-modal)] shadow-xl transform transition-transform duration-[var(--transition-slow)] ease-out"
        role="dialog"
        aria-modal="true"
        aria-label="Menu di navigazione mobile"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h2 className="text-lg font-semibold text-text-primary">Menu</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-text-secondary hover:text-primary hover:bg-[var(--color-surface-elevated)] transition-all duration-[var(--transition-fast)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:ring-offset-[var(--color-surface)]"
            aria-label="Chiudi menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex flex-col p-4 space-y-2" aria-label="Menu principale">
          {navLinks.map(({ path, label }) => (
            <Link
              key={path}
              to={path}
              className={`px-4 py-3 rounded-lg text-base font-medium transition-all duration-[var(--transition-base)] ${
                isActive(path)
                  ? "text-primary bg-[var(--color-primary)]/10 border-l-4 border-[var(--color-primary)]"
                  : "text-text-secondary hover:text-primary hover:bg-[var(--color-surface-elevated)] border-l-4 border-transparent"
              }`}
              aria-current={isActive(path) ? "page" : undefined}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Footer Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[var(--color-border)] bg-[var(--color-surface-elevated)]">
          <p className="text-xs text-text-tertiary text-center">
            Crea Lavoro - Opportunità per studenti e laureati
          </p>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
